export interface SharedAdventureCard {
	item_id: string;
	name: string;
	url: string;
	picture_url: string;
	video_url: string;
	poster_url: string;
	level_begin: string;
	level_end: string;
	credit_label: string;
	is_free: boolean;
	price: number;
	price_f: string;
	release_date: string;
	parent_name?: string;
	parent_url?: string;
	text: string;
}

const CLAMP_2 = 'display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;';
const CLAMP_1 = 'display:-webkit-box;-webkit-line-clamp:1;-webkit-box-orient:vertical;overflow:hidden;';

export function renderAdventureCardElement(card: SharedAdventureCard, levelIconHtml: string): HTMLElement {
	const hasVideo = !!card.video_url;

	const col = document.createElement('div');
	col.className = 'col-12 col-md-6 col-lg-4 col-xl-2 p-2';

	const link = document.createElement('a');
	link.href = card.url;
	link.className =
		'card h-100 text-left shadow-sm text-decoration-none text-reset d-flex flex-column ft-adventure-catalog-card'
		+ (hasVideo ? ' video-card-hover' : '');
	link.setAttribute('data-bs-theme', 'dark');

	const cover = document.createElement('div');
	cover.className = 'overflow-hidden flex-shrink-0';
	cover.setAttribute('style', 'aspect-ratio: 10/13;');
	if (hasVideo) {
		const video = document.createElement('video');
		video.className = 'w-100 h-100';
		video.src = card.video_url;
		video.muted = true;
		video.setAttribute('playsinline', '');
		video.setAttribute('preload', 'metadata');
		if (card.poster_url) video.setAttribute('poster', card.poster_url);
		video.setAttribute('style', 'object-fit: cover; object-position: top;');
		video.setAttribute('aria-hidden', 'true');
		cover.appendChild(video);
	} else {
		const img = document.createElement('img');
		img.src = card.picture_url || '';
		img.className = 'w-100 h-100';
		img.alt = card.name || 'Adventure';
		img.setAttribute('style', 'object-fit: cover; object-position: top;');
		img.loading = 'lazy';
		img.decoding = 'async';
		cover.appendChild(img);
	}
	link.appendChild(cover);

	const body = document.createElement('div');
	body.className = 'card-body d-flex flex-column flex-grow-1 py-2 px-3';

	const title = document.createElement('h6');
	title.className = 'card-title small fw-semibold mb-2 text-break';
	title.setAttribute('style', CLAMP_2);
	title.textContent = card.name || 'Untitled';
	body.appendChild(title);

	const meta = document.createElement('div');
	meta.className = 'mt-auto d-flex flex-column gap-1 small';

	const levelRow = document.createElement('div');
	const levelIcon = document.createElement('span');
	levelIcon.innerHTML = levelIconHtml;
	levelRow.appendChild(levelIcon);
	levelRow.appendChild(document.createTextNode(' '));
	const beginBadge = document.createElement('span');
	beginBadge.className = 'badge bg-primary';
	beginBadge.textContent = card.level_begin;
	levelRow.appendChild(beginBadge);
	levelRow.appendChild(document.createTextNode(' -> '));
	const endBadge = document.createElement('span');
	endBadge.className = 'badge bg-secondary';
	endBadge.textContent = card.level_end;
	levelRow.appendChild(endBadge);
	meta.appendChild(levelRow);

	if (card.credit_label) {
		const credit = document.createElement('div');
		credit.className = 'text-muted lh-sm';
		credit.setAttribute('style', CLAMP_1);
		credit.textContent = card.credit_label;
		meta.appendChild(credit);
	}

	// Nested adventures: "Part of: <bundle>" tag. The whole card is an <a>, so the
	// parent jump is a role=link span (nested anchors are invalid HTML).
	if (card.parent_name) {
		const parent = document.createElement('div');
		parent.className = 'text-muted lh-sm';
		parent.setAttribute('style', CLAMP_1);
		parent.appendChild(document.createTextNode('Part of: '));
		const parentName = document.createElement('span');
		parentName.textContent = card.parent_name;
		const parentUrl = card.parent_url || '';
		if (parentUrl) {
			parentName.className = 'link-primary text-decoration-underline';
			parentName.setAttribute('role', 'link');
			parentName.tabIndex = 0;
			const goToParent = (event: Event): void => {
				event.preventDefault();
				event.stopPropagation();
				window.location.href = parentUrl;
			};
			parentName.addEventListener('click', goToParent);
			parentName.addEventListener('keydown', (event) => {
				if ((event as KeyboardEvent).key === 'Enter') goToParent(event);
			});
		}
		parent.appendChild(parentName);
		meta.appendChild(parent);
	}
	body.appendChild(meta);
	link.appendChild(body);

	const footer = document.createElement('div');
	footer.className =
		'card-footer ft-adventure-catalog-card__footer d-flex align-items-center justify-content-between gap-2 px-3 py-2 border-top border-0 border-opacity-25';
	const price = document.createElement('span');
	price.className = 'ft-adventure-catalog-card__price fw-semibold lh-sm';
	const priceInner = document.createElement('span');
	if (card.is_free) {
		priceInner.className = 'text-success';
		priceInner.textContent = 'Free';
	} else {
		priceInner.className = 'text-warning';
		priceInner.textContent = '$' + card.price_f;
	}
	price.appendChild(priceInner);
	footer.appendChild(price);

	if (card.release_date) {
		const release = document.createElement('span');
		release.className = 'ft-adventure-catalog-card__release text-muted small lh-sm text-end';
		release.textContent = card.release_date;
		footer.appendChild(release);
	}
	link.appendChild(footer);

	col.appendChild(link);
	return col;
}
